import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Phone, ArrowRight, CheckCircle, Clock, Shield, MapPin, CreditCard,
  Zap, Award, ThumbsUp, Star, Wrench, Home, Building, Store, HelpCircle,
  Calculator, Minus, Plus
} from 'lucide-react';
import { companyInfo, testimonials } from '../../data/mock';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { FAQSection, InternalLinksSection } from '../seo/FAQSection';
import { Slider } from '../ui/slider';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';

// Composant calculateur de financement interactif
const FinancingCalculator = () => {
  const [nbFenetres, setNbFenetres] = useState(3);
  const [typeFenetre, setTypeFenetre] = useState('pvc');
  const [duree, setDuree] = useState(12);
  
  // Prix moyens par type de fenêtre (pose comprise)
  const prixParType = {
    pvc: 450,
    aluminium: 750,
    bois: 650,
    mixte: 900
  };
  
  const nomTypes = {
    pvc: 'PVC',
    aluminium: 'Aluminium',
    bois: 'Bois',
    mixte: 'Mixte Bois/Alu'
  };
  
  const montantTotal = nbFenetres * prixParType[typeFenetre];
  const mensualite = Math.round(montantTotal / duree);
  
  return (
    <div className="space-y-6">
      {/* Nombre de fenêtres */}
      <div>
        <label className="block text-sm font-medium mb-3">
          Nombre de fenêtres à remplacer
        </label>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setNbFenetres(Math.max(1, nbFenetres - 1))}
            className="w-10 h-10 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center hover:bg-[hsl(207,78%,90%)] transition-colors"
          >
            <Minus className="w-4 h-4 text-[hsl(207,78%,42%)]" />
          </button>
          <span className="text-3xl font-bold w-12 text-center">{nbFenetres}</span>
          <button
            onClick={() => setNbFenetres(Math.min(20, nbFenetres + 1))}
            className="w-10 h-10 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center hover:bg-[hsl(207,78%,90%)] transition-colors"
          >
            <Plus className="w-4 h-4 text-[hsl(207,78%,42%)]" />
          </button>
        </div>
      </div>
      
      {/* Type de fenêtre */}
      <div>
        <label className="block text-sm font-medium mb-3">
          Type de menuiserie
        </label>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(nomTypes).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTypeFenetre(key)}
              className={`p-3 rounded-lg text-sm font-medium transition-all ${
                typeFenetre === key
                  ? 'bg-[hsl(207,78%,42%)] text-white'
                  : 'bg-[hsl(210,20%,98%)] hover:bg-[hsl(210,20%,95%)]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Durée de financement */}
      <div>
        <label className="block text-sm font-medium mb-3">
          Durée de financement : <span className="text-[hsl(207,78%,42%)]">{duree} mois</span>
        </label>
        <div className="flex gap-2">
          {[6, 12, 18, 24].map((d) => (
            <button
              key={d}
              onClick={() => setDuree(d)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                duree === d
                  ? 'bg-[hsl(24,95%,53%)] text-white'
                  : 'bg-[hsl(210,20%,98%)] hover:bg-[hsl(210,20%,95%)]'
              }`}
            >
              {d} mois
            </button>
          ))}
        </div>
      </div>
      
      {/* Résultat */}
      <div className="bg-gradient-to-br from-[hsl(207,78%,42%)] to-[hsl(207,78%,35%)] text-white rounded-xl p-6">
        <div className="text-center">
          <p className="text-white/80 text-sm mb-1">Estimation de votre projet</p>
          <p className="text-4xl font-bold mb-4">{montantTotal.toLocaleString('fr-FR')} €</p>
          <div className="bg-white/20 rounded-lg p-4">
            <p className="text-white/80 text-sm">Soit seulement</p>
            <p className="text-3xl font-bold text-[hsl(24,95%,53%)]">{mensualite} €<span className="text-lg">/mois</span></p>
            <p className="text-white/70 text-xs mt-1">pendant {duree} mois</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const RenovationFenetresPage = () => {
  // SEO Meta optimisé pour 1ère page Google
  const pageTitle = "Rénovation fenêtres paiement plusieurs fois | Villiers 94";
  const pageDescription = "Rénovation fenêtres PVC, aluminium, bois à Villiers-sur-Marne. Paiement en plusieurs fois sans frais. Devis gratuit ☎ 07 81 55 90 50. Pose fenêtres Val-de-Marne.";
  const pageKeywords = "rénovation fenêtres villiers-sur-marne, remplacement fenêtres 94, pose fenêtres pvc aluminium bois, paiement plusieurs fois fenêtres, devis vitrier villiers-sur-marne, bris de glace villiers-sur-marne, vitrier villiers-sur-marne";
  const canonical = "https://www.menuisiervitrier.fr/renovation-fenetres-paiement-plusieurs-fois-villiers-sur-marne";

  // Force update title and canonical on mount
  useEffect(() => {
    document.title = pageTitle;
    
    // Update or create canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical;
    
    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.content = pageDescription;
    }
  }, [pageTitle, canonical, pageDescription]);

  // Images optimisées SEO avec ALT text - Noms de fichiers optimisés pour le référencement local
  const images = {
    hero: {
      src: "https://customer-assets.emergentagent.com/job_ngl-services/artifacts/y3200q3b_Vitrier-remplacement-fenetre-villiers-sur-marne.jpeg",
      alt: "Vitrier Villiers-sur-Marne – Remplacement de fenêtres PVC aluminium"
    },
    fenetrePvc: {
      src: "https://customer-assets.emergentagent.com/job_ngl-services/artifacts/ulgfo3dv_Vitrier-villiers-sur-marne-94-val-de-marne-fenetre-pvc.jpg",
      alt: "Installation fenêtre PVC double vitrage Villiers-sur-Marne 94"
    },
    chantier: {
      src: "https://customer-assets.emergentagent.com/job_ngl-services/artifacts/xgkgrxca_image00014.jpeg",
      alt: "Pose de fenêtres rénovation maison Villiers-sur-Marne Val-de-Marne"
    },
    // Nouvelles images SEO optimisées
    miroitier: {
      src: "https://customer-assets.emergentagent.com/job_ngl-services/artifacts/sylse417_Vitrier-miroitier-fenetre-villiers-sur-marne.jpeg",
      alt: "Vitrier miroitier fenêtre Villiers-sur-Marne – NGL SERVICES"
    },
    miroitierTel: {
      src: "https://customer-assets.emergentagent.com/job_ngl-services/artifacts/h3ni0m4d_Vitrier-miroitier-fenetre-villiers-sur-marne-0781559050.jpeg",
      alt: "Vitrier miroitier fenêtre Villiers-sur-Marne 07 81 55 90 50"
    },
    vitreCassee: {
      src: "https://customer-assets.emergentagent.com/job_ngl-services/artifacts/4s1rcrbf_Vitrier-vitre-cass%C3%A9-fenetre-villiers-sur-marne-0781559050.jpeg",
      alt: "Vitrerie urgence vitre cassée Villiers-sur-Marne 94350"
    },
    nglServices: {
      src: "https://customer-assets.emergentagent.com/job_ngl-services/artifacts/jt3oxakj_Vitrier-vitre-cass%C3%A9-fenetre-villiers-sur-marne-ngl-services.jpeg",
      alt: "Installation fenêtres Val-de-Marne 94 – NGL SERVICES vitrier"
    }
  };

  // Types de fenêtres proposés
  const typesMenuiseries = [
    {
      title: "Fenêtres PVC",
      description: "Le meilleur rapport qualité-prix. Excellente isolation thermique et phonique, entretien minimal, grande durabilité. Idéal pour les rénovations à budget maîtrisé.",
      features: ["Isolation thermique optimale", "Entretien minimal", "Prix compétitif", "Durabilité 30+ ans"]
    },
    {
      title: "Fenêtres Aluminium",
      description: "Design moderne et finesse des profilés. Résistance exceptionnelle, large choix de coloris RAL. Parfait pour les grandes baies vitrées et maisons contemporaines.",
      features: ["Design contemporain", "Profilés fins", "100% recyclable", "Large choix couleurs"]
    },
    {
      title: "Fenêtres Bois",
      description: "L'authenticité et le charme naturel. Excellente performance thermique naturelle, matériau écologique. Idéal pour les maisons de caractère et bâtiments classés.",
      features: ["Charme authentique", "Isolation naturelle", "Écologique", "Patrimoine préservé"]
    },
    {
      title: "Fenêtres Mixte Bois/Alu",
      description: "Le meilleur des deux mondes. Intérieur bois chaleureux, extérieur aluminium sans entretien. Solution premium pour les projets haut de gamme.",
      features: ["Intérieur chaleureux", "Extérieur durable", "Performance maximale", "Solution premium"]
    }
  ];

  // Types de pose
  const typesPose = [
    {
      title: "Pose en rénovation (sur dormant existant)",
      description: "La solution rapide et économique. On conserve le cadre existant, intervention en quelques heures sans travaux de maçonnerie. Idéal pour le remplacement de fenêtres en bon état structurel.",
      avantages: ["Installation rapide (2-3h/fenêtre)", "Pas de travaux de finition", "Économique", "Moins de désagréments"]
    },
    {
      title: "Pose en dépose totale (remplacement complet)",
      description: "La solution optimale pour une performance maximale. Retrait complet de l'ancienne fenêtre, pose d'un cadre neuf. Recommandée quand le dormant est abîmé ou pour une isolation parfaite.",
      avantages: ["Isolation maximale", "Meilleure étanchéité", "Nouveau cadre", "Durabilité optimale"]
    }
  ];

  // FAQ pour rich snippets
  const faqRenovation = [
    {
      question: "Peut-on payer une rénovation de fenêtres en plusieurs fois ?",
      answer: "Oui, NGL SERVICES propose un paiement en plusieurs fois sans frais pour vos travaux de rénovation de fenêtres à Villiers-sur-Marne. Nous travaillons avec des organismes de financement agréés pour vous permettre d'étaler le coût de vos fenêtres sur 3 à 24 mois selon vos besoins. Un conseiller étudiera votre dossier lors du devis gratuit."
    },
    {
      question: "Quel est le prix d'un remplacement de fenêtre à Villiers-sur-Marne ?",
      answer: "Le prix d'un remplacement de fenêtre à Villiers-sur-Marne varie selon le matériau (PVC, aluminium, bois), les dimensions, le type de vitrage et le mode de pose. Comptez entre 300€ et 800€ pour une fenêtre PVC standard, 500€ à 1200€ pour l'aluminium. NGL SERVICES vous propose un devis gratuit et personnalisé pour estimer précisément votre projet."
    },
    {
      question: "Faut-il un permis pour changer ses fenêtres ?",
      answer: "En règle générale, le remplacement de fenêtres à l'identique ne nécessite pas de permis. Cependant, si vous modifiez l'aspect extérieur (couleur, forme, dimensions), une déclaration préalable de travaux peut être exigée, notamment en zone protégée ou en copropriété. NGL SERVICES vous conseille sur les démarches administratives lors de votre projet à Villiers-sur-Marne."
    },
    {
      question: "Quelle est la différence entre PVC, aluminium et bois ?",
      answer: "Le PVC offre le meilleur rapport qualité-prix avec une excellente isolation et un entretien minimal. L'aluminium permet des profilés plus fins, idéaux pour les grandes surfaces vitrées et les designs contemporains. Le bois apporte chaleur et authenticité, parfait pour les maisons de caractère. NGL SERVICES vous guide vers le choix le plus adapté à votre projet à Villiers-sur-Marne."
    },
    {
      question: "Combien de temps dure la pose de fenêtres ?",
      answer: "La durée de pose dépend du type d'intervention. En pose sur dormant existant (rénovation), comptez 2 à 3 heures par fenêtre. En dépose totale, prévoyez une demi-journée par ouverture. Pour un projet complet de rénovation de 5 à 10 fenêtres, nos équipes NGL SERVICES interviennent généralement sur 1 à 2 jours à Villiers-sur-Marne."
    },
    {
      question: "Proposez-vous le devis gratuit ?",
      answer: "Oui, NGL SERVICES propose un devis 100% gratuit et sans engagement pour tous vos projets de rénovation de fenêtres à Villiers-sur-Marne et dans le Val-de-Marne. Notre technicien se déplace à votre domicile pour prendre les mesures exactes, évaluer vos besoins et vous proposer la solution la plus adaptée avec un devis détaillé."
    },
    {
      question: "Quelles aides financières pour changer ses fenêtres en 2025 ?",
      answer: "En 2025, plusieurs aides existent pour financer vos fenêtres : MaPrimeRénov' (jusqu'à 100€/fenêtre), les CEE (Certificats d'Économie d'Énergie), l'éco-PTZ (prêt à taux zéro), la TVA réduite à 5,5%. NGL SERVICES vous accompagne dans les démarches administratives pour maximiser vos aides à Villiers-sur-Marne."
    },
    {
      question: "Intervenez-vous pour les urgences vitrier à Villiers-sur-Marne ?",
      answer: "Oui, NGL SERVICES assure un service d'urgence vitrier 24h/24 et 7j/7 à Villiers-sur-Marne pour les bris de glace, vitres cassées et sécurisation de locaux. Intervention rapide en moins d'1 heure pour protéger votre habitation. Appelez le 07 81 55 90 50 pour une urgence."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* SEO Head optimisé */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={images.hero.src} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content="fr_FR" />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="FR-94" />
        <meta name="geo.placename" content="Villiers-sur-Marne" />
        <meta name="geo.position" content="48.8273;2.5449" />
        
        {/* Schema.org LocalBusiness + Service */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "NGL SERVICES - Rénovation Fenêtres Villiers-sur-Marne",
            "image": "${images.hero.src}",
            "description": "Rénovation de fenêtres avec facilité de paiement à Villiers-sur-Marne (94350). PVC, aluminium, bois. Paiement en plusieurs fois. Devis gratuit.",
            "url": "${canonical}",
            "telephone": "+33781559050",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Villiers-sur-Marne",
              "postalCode": "94350",
              "addressRegion": "Val-de-Marne",
              "addressCountry": "FR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 48.8273,
              "longitude": 2.5449
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
              "opens": "08:00",
              "closes": "19:00"
            },
            "priceRange": "€€",
            "paymentAccepted": ["Cash", "Credit Card", "Bank Transfer", "Financing"],
            "areaServed": ["Villiers-sur-Marne", "Champigny-sur-Marne", "Bry-sur-Marne", "Nogent-sur-Marne", "Le Plessis-Trévise", "Noisy-le-Grand"]
          }
        `}</script>
        
        {/* FAQPage Schema */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": ${JSON.stringify(faqRenovation.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            })))}
          }
        `}</script>
      </Helmet>

      {/* Hero Section avec image optimisée */}
      <section className="relative min-h-[75vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.hero.src})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(215,25%,15%)]/90 to-[hsl(215,25%,15%)]/70" />
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumb SEO */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="text-white/70 hover:text-white">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/services/fenetres" className="text-white/70 hover:text-white">Menuiserie</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">Rénovation fenêtres</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <Badge className="bg-[hsl(24,95%,53%)] text-white mb-4 px-4 py-2">
            <CreditCard className="w-4 h-4 mr-2" /> Paiement en plusieurs fois disponible
          </Badge>
          
          {/* H1 unique et optimisé */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Rénovation de fenêtres avec facilité de paiement<br />
            à <span className="text-[hsl(24,95%,53%)]">Villiers-sur-Marne</span> (94350)
          </h1>
          
          <p className="text-xl text-gray-200 mb-4">
            <strong>Fenêtres PVC • Aluminium • Bois • Pose sur-mesure • Val-de-Marne (94)</strong>
          </p>
          <p className="text-lg text-gray-300 mb-8 max-w-3xl">
            NGL SERVICES, votre spécialiste de la <strong>rénovation de fenêtres à Villiers-sur-Marne</strong>. 
            Profitez de notre solution de <strong>paiement en plusieurs fois</strong> pour remplacer vos 
            fenêtres sans impacter votre budget. Devis gratuit et conseils personnalisés pour votre projet 
            de menuiserie dans tout le <strong>Val-de-Marne</strong>.
          </p>

          <div className="flex flex-wrap gap-4 mb-8">
            <Link to="/contact">
              <Button size="lg" className="emergency-btn rounded-full text-lg px-8">
                <ArrowRight className="w-5 h-5 mr-2" />
                Demander un devis gratuit – Paiement en plusieurs fois
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full text-lg px-8">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phoneEmergency}
              </Button>
            </a>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center gap-6 text-white/90">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[hsl(24,95%,53%)]" />
              <span>Paiement 3 à 24 mois</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[hsl(24,95%,53%)]" />
              <span>Devis gratuit</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[hsl(24,95%,53%)]" />
              <span>Garantie 10 ans</span>
            </div>
          </div>
        </div>
      </section>

      {/* Bandeau CTA */}
      <section className="py-6 bg-[hsl(207,78%,42%)] text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <CreditCard className="w-8 h-8" />
              <div>
                <p className="font-bold text-lg">Financez vos fenêtres sans effort</p>
                <p className="text-white/90">Paiement en 3, 6, 12 ou 24 fois – Sans frais</p>
              </div>
            </div>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[hsl(207,78%,42%)] hover:bg-gray-100 rounded-full font-bold">
                <ArrowRight className="w-5 h-5 mr-2" />
                Simuler mon financement
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SECTION SEO #1 - Pourquoi rénover ===== */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-6">
                Pourquoi rénover ses fenêtres à Villiers-sur-Marne ?
              </h2>
              
              <p className="text-muted-foreground mb-4">
                Le <strong>remplacement de fenêtres à Villiers-sur-Marne</strong> représente l'un des 
                investissements les plus rentables pour votre logement. Les anciennes fenêtres sont 
                responsables de jusqu'à <strong>25% des déperditions thermiques</strong> d'une habitation. 
                En optant pour des fenêtres modernes avec double ou triple vitrage, vous réalisez des 
                économies significatives sur vos factures d'énergie tout en améliorant votre confort.
              </p>
              
              <p className="text-muted-foreground mb-4">
                À <strong>Villiers-sur-Marne (94350)</strong>, ville résidentielle du Val-de-Marne, de 
                nombreuses maisons et appartements datent des années 60-80 et disposent encore de 
                fenêtres simple vitrage ou de menuiseries vieillissantes. La <strong>rénovation de 
                fenêtres</strong> permet de moderniser l'esthétique de votre façade, d'améliorer 
                l'isolation acoustique face aux nuisances sonores, et de renforcer la sécurité de 
                votre domicile.
              </p>

              <p className="text-muted-foreground mb-6">
                Chez <strong>NGL SERVICES</strong>, nous comprenons que le coût peut être un frein. 
                C'est pourquoi nous proposons une <strong>facilité de paiement en plusieurs fois</strong> 
                pour étaler votre investissement sur 3 à 24 mois. Vous pouvez ainsi bénéficier 
                immédiatement du confort de fenêtres neuves tout en maîtrisant votre budget mensuel.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Zap, text: "Économies d'énergie jusqu'à 30%" },
                  { icon: Shield, text: "Sécurité renforcée anti-intrusion" },
                  { icon: ThumbsUp, text: "Confort thermique été/hiver" },
                  { icon: Award, text: "Valorisation immobilière +10%" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-[hsl(210,20%,98%)] rounded-lg">
                    <item.icon className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                    <span className="text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={images.fenetrePvc.src}
                alt={images.fenetrePvc.alt}
                className="w-full h-auto rounded-2xl shadow-xl"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[hsl(24,95%,53%)] flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[hsl(215,25%,15%)]">Paiement facilité</p>
                    <p className="text-sm text-muted-foreground">3 à 24 mois sans frais</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION SEO #2 - Types de fenêtres ===== */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Quels types de fenêtres proposons-nous à Villiers-sur-Marne ?
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              NGL SERVICES vous accompagne dans le choix de vos nouvelles fenêtres. PVC, aluminium, 
              bois ou mixte : chaque matériau présente des avantages spécifiques selon votre projet 
              de <strong>rénovation à Villiers-sur-Marne</strong>.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {typesMenuiseries.map((type, idx) => (
              <Card key={idx} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[hsl(215,25%,15%)] mb-3">{type.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{type.description}</p>
                  <ul className="space-y-2">
                    {type.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-[hsl(24,95%,53%)]" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION SEO #3 - Types de pose ===== */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={images.chantier.src}
                alt={images.chantier.alt}
                className="w-full h-auto rounded-2xl shadow-xl"
                loading="lazy"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-6">
                Pose de fenêtres : rénovation ou dépose totale ?
              </h2>
              
              <p className="text-muted-foreground mb-6">
                Deux techniques de pose s'offrent à vous pour le <strong>remplacement de vos fenêtres 
                à Villiers-sur-Marne</strong>. Notre équipe de techniciens NGL SERVICES évalue l'état 
                de vos menuiseries existantes pour vous recommander la solution la plus adaptée.
              </p>

              <div className="space-y-6">
                {typesPose.map((pose, idx) => (
                  <div key={idx} className="p-5 bg-[hsl(210,20%,98%)] rounded-xl">
                    <h3 className="text-lg font-bold text-[hsl(215,25%,15%)] mb-2">{pose.title}</h3>
                    <p className="text-muted-foreground text-sm mb-3">{pose.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {pose.avantages.map((avantage, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {avantage}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION SEO #4 - Types de bâtiments ===== */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Rénovation fenêtres maison, appartement ou commerce
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              NGL SERVICES intervient sur tous types de bâtiments à Villiers-sur-Marne et dans 
              le Val-de-Marne. Particuliers et professionnels, nous adaptons notre expertise 
              à chaque configuration.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Home,
                title: "Maisons individuelles",
                description: "Rénovation complète ou partielle de vos fenêtres de maison. Nous intervenons sur tous les types d'ouvertures : fenêtres, portes-fenêtres, baies vitrées, fenêtres de toit. Devis personnalisé selon vos priorités et votre budget à Villiers-sur-Marne."
              },
              {
                icon: Building,
                title: "Appartements & copropriétés",
                description: "Remplacement de fenêtres en appartement avec respect des règles de copropriété. Nous vous accompagnons dans les démarches administratives (autorisation AG, déclaration préalable) et coordonnons l'intervention avec le syndic si nécessaire."
              },
              {
                icon: Store,
                title: "Commerces & bureaux",
                description: "Solutions professionnelles pour vitrines, devantures et menuiseries de bureaux. Interventions possibles en dehors des heures d'ouverture pour ne pas perturber votre activité. Sécurisation et modernisation de vos locaux à Villiers-sur-Marne."
              }
            ].map((item, idx) => (
              <Card key={idx} className="border-0 shadow-lg text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-8 h-8 text-[hsl(207,78%,42%)]" />
                  </div>
                  <h3 className="text-xl font-bold text-[hsl(215,25%,15%)] mb-3">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION SEO #5 - Calculateur de financement interactif ===== */}
      <section className="py-16 bg-gradient-to-br from-[hsl(207,78%,42%)] to-[hsl(207,78%,35%)] text-white" id="calculateur-financement">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Simulateur de financement fenêtres – Villiers-sur-Marne (94)
            </h2>
            <p className="text-lg text-white/90 max-w-3xl mx-auto">
              Calculez vos mensualités pour la <strong>rénovation de vos fenêtres à Villiers-sur-Marne</strong>. 
              NGL SERVICES propose un <strong>paiement en plusieurs fois</strong> adapté à votre budget.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Calculateur interactif */}
            <div className="bg-white rounded-2xl p-8 text-[hsl(215,25%,15%)]">
              <h3 className="text-xl font-bold mb-6 text-center">
                Calculez vos mensualités
              </h3>
              
              <FinancingCalculator />
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Link to="/contact">
                  <Button className="w-full emergency-btn rounded-full py-6 text-lg">
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Demander un devis personnalisé
                  </Button>
                </Link>
                <p className="text-xs text-center text-muted-foreground mt-3">
                  *Simulation indicative. Taux et conditions selon dossier.
                </p>
              </div>
            </div>
            
            {/* Contenu SEO enrichi */}
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-bold mb-4">
                  Financement fenêtres Villiers-sur-Marne – Comment ça marche ?
                </h3>
                <p className="text-white/90 mb-4">
                  Chez <strong>NGL SERVICES</strong>, nous comprenons que la <strong>rénovation de fenêtres</strong> 
                  représente un investissement important. C'est pourquoi nous proposons une solution de 
                  <strong> financement en plusieurs fois</strong> pour rendre vos projets accessibles à tous les 
                  habitants de <strong>Villiers-sur-Marne</strong> et du <strong>Val-de-Marne (94)</strong>.
                </p>
                <p className="text-white/90">
                  Notre partenariat avec des organismes de crédit agréés vous permet d'étaler le coût de vos 
                  nouvelles <strong>fenêtres PVC, aluminium ou bois</strong> sur <strong>3 à 24 mensualités</strong>. 
                  Profitez immédiatement du confort thermique et acoustique tout en maîtrisant votre budget mensuel.
                </p>
              </div>
              
              <div>
                <h4 className="font-bold mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                  Avantages du financement NGL SERVICES
                </h4>
                <ul className="space-y-2 text-white/90">
                  {[
                    "Réponse rapide sous 24h après étude du dossier",
                    "Taux compétitifs négociés avec nos partenaires",
                    "Pas de frais de dossier pour votre demande",
                    "Première mensualité différée (jusqu'à 3 mois)",
                    "Assurance décès-invalidité optionnelle incluse",
                    "Cumul possible avec MaPrimeRénov' et CEE"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-[hsl(24,95%,53%)]">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h4 className="font-bold mb-3">
                  Prix indicatifs fenêtres Val-de-Marne (94)
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-white/10 p-3 rounded-lg">
                    <p className="text-white/70">Fenêtre PVC standard</p>
                    <p className="font-bold">300€ – 600€</p>
                  </div>
                  <div className="bg-white/10 p-3 rounded-lg">
                    <p className="text-white/70">Fenêtre aluminium</p>
                    <p className="font-bold">500€ – 1 000€</p>
                  </div>
                  <div className="bg-white/10 p-3 rounded-lg">
                    <p className="text-white/70">Porte-fenêtre PVC</p>
                    <p className="font-bold">600€ – 1 200€</p>
                  </div>
                  <div className="bg-white/10 p-3 rounded-lg">
                    <p className="text-white/70">Baie vitrée coulissante</p>
                    <p className="font-bold">1 200€ – 2 500€</p>
                  </div>
                </div>
                <p className="text-xs text-white/60 mt-3">
                  *Prix TTC indicatifs, pose comprise. Devis gratuit sur mesure.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION GALERIE IMAGES SEO ===== */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Nos réalisations – Vitrier Villiers-sur-Marne
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Découvrez quelques exemples de nos interventions de <strong>rénovation de fenêtres</strong> 
              et <strong>vitrerie</strong> à <strong>Villiers-sur-Marne</strong> et dans le <strong>Val-de-Marne (94)</strong>.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                src: images.miroitier.src, 
                alt: images.miroitier.alt,
                title: "Miroiterie sur mesure"
              },
              { 
                src: images.miroitierTel.src, 
                alt: images.miroitierTel.alt,
                title: "Installation fenêtres"
              },
              { 
                src: images.vitreCassee.src, 
                alt: images.vitreCassee.alt,
                title: "Urgence vitre cassée"
              },
              { 
                src: images.nglServices.src, 
                alt: images.nglServices.alt,
                title: "Rénovation complète"
              }
            ].map((img, idx) => (
              <div key={idx} className="group relative overflow-hidden rounded-xl shadow-lg">
                <img 
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
                  <p className="text-white font-medium p-4">{img.title}</p>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-muted-foreground mt-6">
            <strong>NGL SERVICES</strong> – Vitrier à Villiers-sur-Marne (94350) – Intervention rapide – Devis gratuit
          </p>
        </div>
      </section>

      {/* ===== SECTION SEO #6 - Pourquoi NGL SERVICES ===== */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Pourquoi choisir NGL SERVICES à Villiers-sur-Marne ?
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Entreprise locale implantée dans le Val-de-Marne, NGL SERVICES met son expertise 
              et son savoir-faire au service de votre projet de rénovation de fenêtres.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: MapPin,
                title: "Proximité locale",
                description: "Basés à Villiers-sur-Marne, nous intervenons rapidement dans tout le Val-de-Marne (94). Connaissance du patrimoine local et des réglementations."
              },
              {
                icon: Award,
                title: "Expertise certifiée",
                description: "Équipe de techniciens qualifiés RGE. Formation continue sur les dernières techniques de pose et les nouveaux produits."
              },
              {
                icon: CreditCard,
                title: "Solutions financement",
                description: "Facilité de paiement en plusieurs fois adaptée à votre budget. Accompagnement dans les demandes d'aides (MaPrimeRénov', CEE)."
              },
              {
                icon: Shield,
                title: "Garanties solides",
                description: "Garantie décennale sur la pose. Garantie fabricant 10 ans sur les menuiseries. SAV réactif et disponible."
              }
            ].map((item, idx) => (
              <Card key={idx} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-[hsl(24,95%,93%)] flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-[hsl(24,95%,53%)]" />
                  </div>
                  <h3 className="text-lg font-bold text-[hsl(215,25%,15%)] mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION FAQ SEO ===== */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Questions fréquentes – Rénovation fenêtres Villiers-sur-Marne
            </h2>
            <p className="text-lg text-muted-foreground">
              Retrouvez les réponses aux questions les plus courantes sur la rénovation de fenêtres
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <FAQSection faqs={faqRenovation} />
          </div>
        </div>
      </section>

      {/* ===== CTA Final ===== */}
      <section className="py-20 bg-[hsl(215,25%,15%)] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Prêt à rénover vos fenêtres ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Demandez votre devis gratuit et découvrez nos solutions de paiement en plusieurs fois 
            pour votre projet de rénovation à Villiers-sur-Marne.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="emergency-btn rounded-full text-lg px-8">
                <ArrowRight className="w-5 h-5 mr-2" />
                Demander un devis gratuit – Paiement en plusieurs fois
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="bg-white text-[hsl(215,25%,15%)] hover:bg-gray-100 rounded-full text-lg px-8">
                <Phone className="w-5 h-5 mr-2" />
                Appeler : {companyInfo.phoneEmergency}
              </Button>
            </a>
          </div>
          
          <p className="mt-8 text-gray-400">
            NGL SERVICES – Votre vitrier et menuisier à Villiers-sur-Marne (94350)
          </p>
        </div>
      </section>
    </div>
  );
};

export default RenovationFenetresPage;
